/* Waitlist.jsx — public early-access signup page for the July marketing
   campaign. Lightweight and separate from Landing.jsx (the full marketing
   site) on purpose: this is the one page campaign links point at while the
   main app isn't public yet. Reads ?src=instagram / tiktok / x / reddit off
   the URL so signups can be tied back to the platform that drove them.

   Also home of the "Founding Manager" referral program: every signup gets a
   personal ?ref= link, and referring friends climbs a reward ladder
   (config/referralProgram.js — tools unlocked early, a badge, founder
   pricing). The ladder UI below is display-only; the backend owns the real
   counts and unlock rules. */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { JAI_COLORS, JAI_FONTS, JaiMark } from '../components/JaiBrand'
import { waitlistApi } from '../services/api'
import { REFERRAL_TIERS } from '../config/referralProgram'

// ── Small pieces ─────────────────────────────────────────────────────────────

const check = (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5l3.2 3L13 4.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/* One rung of the reward ladder. `state` is 'unlocked' | 'next' | 'locked' —
   unlocked rungs go green with a check, the next target gets the blue
   highlight, the rest sit muted until earned. */
function TierRow({ tier, state }) {
  const isUnlocked = state === 'unlocked'
  const isNext = state === 'next'

  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '10px 12px',
      borderRadius: 10,
      background: isUnlocked ? 'rgba(26,135,84,0.10)' : isNext ? 'rgba(58,124,255,0.10)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${isUnlocked ? 'rgba(26,135,84,0.45)' : isNext ? 'rgba(58,124,255,0.35)' : 'rgba(255,255,255,0.08)'}`,
      textAlign: 'left',
    }}>
      <span style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isUnlocked ? JAI_COLORS.success : isNext ? JAI_COLORS.blueBright : 'rgba(255,255,255,0.08)',
        color: 'white',
        fontSize: 12, fontWeight: 700, fontFamily: JAI_FONTS.mono,
        marginTop: 1,
      }}>
        {isUnlocked ? check : tier.referralsNeeded}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: isUnlocked || isNext ? 'white' : 'rgba(255,255,255,0.65)', lineHeight: 1.35 }}>
          {tier.title}
          {isUnlocked && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#5BD68F', letterSpacing: '0.04em' }}>UNLOCKED</span>}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.45, marginTop: 2 }}>
          {tier.referralsNeeded === 1 ? '1 friend joins' : `${tier.referralsNeeded} friends join`} — {tier.detail}
        </div>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Waitlist() {
  const [searchParams] = useSearchParams()
  const source = searchParams.get('src') || 'direct'
  const ref = searchParams.get('ref') || null

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  // Optional, and unticked by default on purpose: marketing email needs the
  // person's own yes, so it can never be pre-selected or required to join.
  const [marketing, setMarketing] = useState(false)
  const [status, setStatus] = useState('idle') // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState('')
  const [count, setCount] = useState(null)

  // Set once this person joins — it's their OWN new code (for inviting
  // others), not the ?ref= code that may have brought them here.
  const [referralCode, setReferralCode] = useState(null)
  // Ladder progress for that code, from GET /api/waitlist/referral-status:
  // { referralCount, unlockedTools, foundingManager, founderPricing,
  //   currentTier, nextTier }. Null until it loads; the ladder renders with
  // zero progress in the meantime, so a failed request costs nothing.
  const [referralStatus, setReferralStatus] = useState(null)
  const [copied, setCopied] = useState(false)
  const referralLink = referralCode ? `${window.location.origin}/waitlist?ref=${referralCode}` : ''

  useEffect(() => {
    waitlistApi.getCount()
      .then((res) => setCount(res.data?.count ?? null))
      .catch(() => {}) // silently ignore — the count is a nice-to-have, not critical
  }, [])

  // Once we know this person's own referral code, load their ladder progress.
  useEffect(() => {
    if (!referralCode) return
    waitlistApi.getReferralStatus(referralCode)
      .then((res) => setReferralStatus(res.data ?? null))
      .catch(() => {})
  }, [referralCode])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (status === 'loading') return
    setStatus('loading')
    setErrorMsg('')
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim()
      const res = await waitlistApi.join(fullName, email, source, ref, marketing)
      setStatus('done')
      setReferralCode(res.data?.referralCode ?? null)
      if (res.data?.alreadyOnList) {
        setCount((c) => c) // no change — they were already counted
      } else {
        setCount((c) => (typeof c === 'number' ? c + 1 : c))
      }
    } catch (err) {
      setStatus('error')
      setErrorMsg(err.response?.data?.error || 'Something went wrong — please try again.')
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API can fail (permissions, non-secure context) — the link
      // is still visible and selectable, so this isn't a dead end.
    }
  }

  const referralCount = referralStatus?.referralCount ?? 0
  const nextTier = referralStatus ? referralStatus.nextTier : REFERRAL_TIERS[0]

  // 'unlocked' | 'next' | 'locked' for each rung of the ladder.
  const tierState = (tier) => {
    if (referralCount >= tier.referralsNeeded) return 'unlocked'
    if (nextTier && tier.tier === nextTier.tier) return 'next'
    return 'locked'
  }

  return (
    <div style={{
      minHeight: 'calc(100vh / var(--jai-zoom))',
      background: `linear-gradient(180deg, ${JAI_COLORS.navyDeep} 0%, ${JAI_COLORS.navy} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: JAI_FONTS.tight,
    }}>
      <div style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <JaiMark size={64} />
        </div>

        <h1 style={{
          color: 'white',
          fontSize: 32,
          fontWeight: 700,
          margin: '0 0 12px',
          letterSpacing: '-0.02em',
        }}>
          JAI is arriving soon.
        </h1>

        <p style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: 16,
          lineHeight: 1.5,
          margin: '0 0 32px',
        }}>
          An AI-powered fantasy sports co-manager, compatible with NFL and NBA
          fantasy leagues at launch. Launching early August 2026 — get early
          access by joining the waitlist.
        </p>

        {status === 'done' ? (
          <>
            <div style={{
              borderLeft: `3px solid ${JAI_COLORS.success}`,
              borderRadius: 0,
              padding: '6px 0 6px 20px',
              color: 'white',
            }}>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>You're locked in.</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                We'll email you the moment early access opens.
              </div>
            </div>

            {referralCode && (
              <div style={{
                marginTop: 14,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 12,
                padding: '20px 24px',
                color: 'white',
              }}>
                <div style={{
                  fontSize: 11.5, fontWeight: 700, letterSpacing: '0.09em',
                  textTransform: 'uppercase', color: JAI_COLORS.blueBright,
                  textAlign: 'left', marginBottom: 6,
                }}>
                  Become a Founding Manager
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, textAlign: 'left', marginBottom: 14 }}>
                  Bring your league. Every friend who joins through your link
                  unlocks JAI tools for you before anyone else gets them.
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <input
                    type="text"
                    readOnly
                    value={referralLink}
                    onFocus={(e) => e.target.select()}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.85)',
                      fontSize: 13,
                      fontFamily: JAI_FONTS.mono,
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: JAI_COLORS.blueBright,
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: JAI_FONTS.tight,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>

                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', textAlign: 'left', marginBottom: 10 }}>
                  {referralCount === 0
                    ? 'No friends yet — your first unlock is one invite away.'
                    : nextTier
                      ? `${referralCount} ${referralCount === 1 ? 'friend has' : 'friends have'} joined — ${nextTier.referralsNeeded - referralCount} more to your next unlock.`
                      : `${referralCount} friends have joined — you've unlocked the whole ladder. Founding Manager status: earned.`}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {REFERRAL_TIERS.map((tier) => (
                    <TierRow key={tier.tier} tier={tier} state={tierState(tier)} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  type="text"
                  required
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={status === 'loading'}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '14px 16px',
                    borderRadius: 10,
                    border: `1px solid rgba(255,255,255,0.2)`,
                    background: 'rgba(255,255,255,0.06)',
                    color: 'white',
                    fontSize: 16,
                    fontFamily: JAI_FONTS.tight,
                    outline: 'none',
                  }}
                />
                <input
                  type="text"
                  required
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={status === 'loading'}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '14px 16px',
                    borderRadius: 10,
                    border: `1px solid rgba(255,255,255,0.2)`,
                    background: 'rgba(255,255,255,0.06)',
                    color: 'white',
                    fontSize: 16,
                    fontFamily: JAI_FONTS.tight,
                    outline: 'none',
                  }}
                />
              </div>
              <input
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'loading'}
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: `1px solid rgba(255,255,255,0.2)`,
                  background: 'rgba(255,255,255,0.06)',
                  color: 'white',
                  fontSize: 16,
                  fontFamily: JAI_FONTS.tight,
                  outline: 'none',
                }}
              />
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                textAlign: 'left',
                color: 'rgba(255,255,255,0.7)',
                fontSize: 13,
                lineHeight: 1.45,
                fontFamily: JAI_FONTS.tight,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  disabled={status === 'loading'}
                  style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: JAI_COLORS.blueBright }}
                />
                <span>Also email me about JAI plans, new features and offers. Optional, and you can unsubscribe any time.</span>
              </label>
              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  padding: '14px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: JAI_COLORS.blueBright,
                  color: 'white',
                  fontSize: 16,
                  fontWeight: 600,
                  fontFamily: JAI_FONTS.tight,
                  cursor: status === 'loading' ? 'default' : 'pointer',
                  opacity: status === 'loading' ? 0.7 : 1,
                }}
              >
                {status === 'loading' ? 'Joining…' : 'Join the waitlist'}
              </button>
              {status === 'error' && (
                <div style={{ color: '#FF8A8A', fontSize: 14, marginTop: 4 }}>{errorMsg}</div>
              )}
            </form>

            {/* Founding Manager teaser — sells the referral ladder BEFORE
                joining, since "what do I get for sharing?" is the main reason
                anyone bothers to invite their league. */}
            <div style={{
              marginTop: 14,
              padding: '14px 16px',
              borderRadius: 0,
              borderLeft: '3px solid rgba(58,124,255,0.8)',
              textAlign: 'left',
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
                textTransform: 'uppercase', color: JAI_COLORS.blueBright,
                marginBottom: 6,
              }}>
                Founding Manager rewards
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                Join, then bring your league — every friend who signs up through
                your link earns you rewards: <strong style={{ color: 'white' }}>1 friend</strong> unlocks
                Trash Talk early, <strong style={{ color: 'white' }}>3</strong> unlocks Power Rankings +
                the Founding Manager badge, <strong style={{ color: 'white' }}>5</strong> locks in founder
                pricing at launch.
              </div>
            </div>
          </>
        )}

        {typeof count === 'number' && count > 0 && (
          <div style={{
            marginTop: 20,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: JAI_COLORS.blueBright }} />
            <span style={{ color: 'white', fontSize: 14 }}>
              <strong>{count.toLocaleString()}</strong> {count === 1 ? 'person has' : 'people have'} already joined
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
